// frontend/src/components/Admin/OfferManagement.jsx   (route: /admin/offers)
// The ONLY place offer rules are set. Everything on the storefront follows what is saved here.
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { fetchAdminProducts } from "../../../redux/slices/adminProductSlice";
import { fetchAdminOffers, saveOffer, deleteOffer } from "../../../redux/slices/adminOfferSlice";

const pad = (n) => String(n).padStart(2, "0");
const toLocalInput = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}T${pad(x.getHours())}:${pad(x.getMinutes())}`;
};
const fmt = (d) =>
  new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const blankForm = () => ({
  id: null,
  name: "",
  bannerText: "Buy 1 Get 1 Free",
  startsAt: toLocalInput(Date.now()),
  endsAt: toLocalInput(Date.now() + 7 * 24 * 3600 * 1000),
  isActive: true,
  showCountdown: true,
  maxFreeItems: 2,
  mode: "all",
  collections: [],
  productIds: [],
});

const formFromOffer = (o) => ({
  id: o._id,
  name: o.name,
  bannerText: o.bannerText || "",
  startsAt: toLocalInput(o.startsAt),
  endsAt: toLocalInput(o.endsAt),
  isActive: !!o.isActive,
  showCountdown: o.showCountdown !== false,
  maxFreeItems: o.maxFreeItems ?? 2,
  mode: o.eligibility?.mode || "all",
  collections: o.eligibility?.collections || [],
  productIds: (o.eligibility?.productIds || []).map(String),
});

const payloadFromForm = (f) => ({
  name: f.name,
  bannerText: f.bannerText,
  startsAt: new Date(f.startsAt).toISOString(),
  endsAt: new Date(f.endsAt).toISOString(),
  isActive: f.isActive,
  showCountdown: f.showCountdown,
  maxFreeItems: Number(f.maxFreeItems),
  eligibility: { mode: f.mode, collections: f.collections, productIds: f.productIds },
});

const statusOf = (o) => {
  const now = Date.now();
  if (new Date(o.endsAt) <= now) return { label: "Ended", cls: "bg-gray-200 text-gray-700" };
  if (!o.isActive) return { label: "Off", cls: "bg-gray-200 text-gray-700" };
  if (new Date(o.startsAt) > now) return { label: "Scheduled", cls: "bg-blue-100 text-blue-800" };
  return { label: "Live", cls: "bg-green-100 text-green-800" };
};

const eligibilityText = (o) => {
  const e = o.eligibility || {};
  if (e.mode === "collections") return `${(e.collections || []).length} collection(s)`;
  if (e.mode === "products") return `${(e.productIds || []).length} product(s)`;
  return "All products";
};

const OfferManagement = () => {
  const dispatch = useDispatch();
  const { offers, loading, saving, error } = useSelector((s) => s.adminOffers);
  const { products } = useSelector((s) => s.adminProducts);
  const [form, setForm] = useState(null); // null = list view
  const [search, setSearch] = useState("");

  useEffect(() => {
    dispatch(fetchAdminOffers());
    dispatch(fetchAdminProducts());
  }, [dispatch]);

  const collectionNames = useMemo(() => {
    const set = new Set();
    (products || []).forEach((p) => (p.collections || []).forEach((c) => c && set.add(c)));
    return [...set].sort();
  }, [products]);

  const visibleProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (products || []).filter((p) => !q || p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q));
  }, [products, search]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleIn = (key, value) =>
    setForm((f) => ({ ...f, [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Give the offer a name");
    if (!form.startsAt || !form.endsAt) return toast.error("Pick a start and end time");
    if (new Date(form.endsAt) <= new Date(form.startsAt)) return toast.error("End time must be after start time");
    const res = await dispatch(saveOffer({ id: form.id, data: payloadFromForm(form) }));
    if (saveOffer.fulfilled.match(res)) {
      toast.success(form.id ? "Offer updated" : "Offer created");
      setForm(null);
      dispatch(fetchAdminOffers());
    } else {
      toast.error(res.payload || "Could not save offer");
    }
  };

  const quickToggle = async (o) => {
    const res = await dispatch(saveOffer({ id: o._id, data: payloadFromForm({ ...formFromOffer(o), isActive: !o.isActive }) }));
    if (saveOffer.fulfilled.match(res)) {
      toast.success(o.isActive ? "Offer switched off" : "Offer switched on");
      dispatch(fetchAdminOffers());
    } else {
      toast.error(res.payload || "Could not update offer");
    }
  };

  const remove = async (o) => {
    if (!window.confirm(`Delete "${o.name}"? Past orders keep their discount record.`)) return;
    const res = await dispatch(deleteOffer(o._id));
    if (deleteOffer.fulfilled.match(res)) toast.success("Offer deleted");
    else toast.error(res.payload || "Could not delete offer");
  };

  // ── FORM ───────────────────────────────────────────────────────────────────
  if (form) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <h2 className="text-2xl font-bold mb-6">{form.id ? "Edit Offer" : "New BOGO Offer"}</h2>
        <form onSubmit={submit} className="space-y-6">
          <div className="bg-gray-50 border rounded p-4 text-sm text-gray-700">
            <p className="font-semibold mb-1">How BOGO works</p>
            <p>
              Customers add any 2 eligible items: the higher price is paid and the cheaper one is free. With 3 or more
              items the cheapest half (rounded down) are free, up to the limit below. Equal prices: either one is free.
              Items that are not eligible are charged in full.
            </p>
          </div>

          <div>
            <label className="block font-semibold mb-1">Offer name (internal)</label>
            <input className="w-full border rounded p-2" value={form.name} maxLength={80}
              onChange={(e) => set({ name: e.target.value })} placeholder="Diwali BOGO 2026" />
          </div>

          <div>
            <label className="block font-semibold mb-1">Banner text (shown on the site)</label>
            <input className="w-full border rounded p-2" value={form.bannerText} maxLength={120}
              onChange={(e) => set({ bannerText: e.target.value })} placeholder="DIWALI BOGO · Buy 1 Get 1 Free" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-semibold mb-1">Starts</label>
              <input type="datetime-local" className="w-full border rounded p-2" value={form.startsAt}
                onChange={(e) => set({ startsAt: e.target.value })} />
            </div>
            <div>
              <label className="block font-semibold mb-1">Ends</label>
              <input type="datetime-local" className="w-full border rounded p-2" value={form.endsAt}
                onChange={(e) => set({ endsAt: e.target.value })} />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block font-semibold mb-1">Max free items per order</label>
              <input type="number" min="0" max="20" step="1" className="w-full border rounded p-2" value={form.maxFreeItems}
                onChange={(e) => set({ maxFreeItems: e.target.value })} />
              <p className="text-xs text-gray-500 mt-1">0 = no limit. 2 means up to 4 items per order get the offer.</p>
            </div>
            <div className="space-y-2 pt-6">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.isActive} onChange={(e) => set({ isActive: e.target.checked })} />
                <span>Enabled (goes live at the start time)</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.showCountdown} onChange={(e) => set({ showCountdown: e.target.checked })} />
                <span>Show countdown in the banner</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block font-semibold mb-2">Which products are eligible?</label>
            <div className="flex flex-wrap gap-4 mb-3">
              {[
                ["all", "All products"],
                ["collections", "Selected collections"],
                ["products", "Selected products"],
              ].map(([val, label]) => (
                <label key={val} className="flex items-center gap-2">
                  <input type="radio" name="mode" checked={form.mode === val} onChange={() => set({ mode: val })} />
                  <span>{label}</span>
                </label>
              ))}
            </div>

            {form.mode === "collections" && (
              <div className="border rounded p-3 grid sm:grid-cols-2 gap-2">
                {collectionNames.length === 0 && (
                  <p className="text-sm text-gray-500">No collections found on your products yet.</p>
                )}
                {collectionNames.map((c) => (
                  <label key={c} className="flex items-center gap-2">
                    <input type="checkbox" checked={form.collections.includes(c)} onChange={() => toggleIn("collections", c)} />
                    <span>{c}</span>
                  </label>
                ))}
              </div>
            )}

            {form.mode === "products" && (
              <div className="border rounded p-3">
                <input className="w-full border rounded p-2 mb-3" placeholder="Search by name or SKU"
                  value={search} onChange={(e) => setSearch(e.target.value)} />
                <p className="text-xs text-gray-500 mb-2">{form.productIds.length} selected</p>
                <div className="max-h-64 overflow-y-auto space-y-1">
                  {visibleProducts.map((p) => (
                    <label key={p._id} className="flex items-center gap-2">
                      <input type="checkbox" checked={form.productIds.includes(String(p._id))}
                        onChange={() => toggleIn("productIds", String(p._id))} />
                      <span>{p.name}</span>
                      <span className="text-xs text-gray-500">₹{p.price} · {p.sku}</span>
                    </label>
                  ))}
                  {visibleProducts.length === 0 && <p className="text-sm text-gray-500">No products match.</p>}
                </div>
              </div>
            )}
          </div>

          {error && <p className="text-red-600 text-sm">{error}</p>}

          <div className="flex gap-3">
            <button type="submit" disabled={saving}
              className="bg-black text-white px-5 py-2 rounded hover:bg-gray-800 disabled:opacity-50">
              {saving ? "Saving…" : "Save offer"}
            </button>
            <button type="button" onClick={() => setForm(null)} className="px-5 py-2 rounded border hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ── LIST ───────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Offers</h2>
        <button onClick={() => setForm(blankForm())} className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800">
          New BOGO offer
        </button>
      </div>

      {loading && <p>Loading …</p>}
      {error && !loading && <p className="text-red-600 mb-4">{error}</p>}

      <div className="overflow-x-auto shadow-md sm:rounded-lg">
        <table className="min-w-full text-left text-gray-500">
          <thead className="bg-gray-100 text-xs">
            <tr>
              <th className="py-3 px-4">Name</th>
              <th className="py-3 px-4">Status</th>
              <th className="py-3 px-4">Runs</th>
              <th className="py-3 px-4">Eligible</th>
              <th className="py-3 px-4">Max free</th>
              <th className="py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {offers.length > 0 ? (
              offers.map((o) => {
                const st = statusOf(o);
                const ended = new Date(o.endsAt) <= Date.now();
                return (
                  <tr key={o._id} className="border-b hover:bg-gray-50">
                    <td className="p-4 font-medium text-gray-900">{o.name}</td>
                    <td className="p-4"><span className={`px-2 py-1 rounded text-xs font-semibold ${st.cls}`}>{st.label}</span></td>
                    <td className="p-4 whitespace-nowrap text-sm">{fmt(o.startsAt)} → {fmt(o.endsAt)}</td>
                    <td className="p-4">{eligibilityText(o)}</td>
                    <td className="p-4">{o.maxFreeItems === 0 ? "No limit" : o.maxFreeItems}</td>
                    <td className="p-4 whitespace-nowrap">
                      <button onClick={() => setForm(formFromOffer(o))}
                        className="bg-yellow-500 text-white px-2 py-1 rounded mr-2 hover:bg-yellow-600">Edit</button>
                      {!ended && (
                        <button onClick={() => quickToggle(o)}
                          className="bg-gray-700 text-white px-2 py-1 rounded mr-2 hover:bg-gray-800">
                          {o.isActive ? "Switch off" : "Switch on"}
                        </button>
                      )}
                      <button onClick={() => remove(o)}
                        className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600">Delete</button>
                    </td>
                  </tr>
                );
              })
            ) : (
              !loading && (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-gray-500">No offers yet. Create your first BOGO offer.</td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OfferManagement;
